import dayjs from "dayjs";

/** Days window for "about to expire" (dashboard banner, filters, responsibilities). */
export const BADGE_ABOUT_TO_EXPIRE_WITHIN_DAYS = 7;

export const BADGE_EXPIRATION_FILTER_ABOUT_TO_EXPIRE = "about_to_expire";
export const BADGE_EXPIRATION_FILTER_NONE = "none";

/** Route query key when deep-linking to Your Badges with expiration filter. */
export const BADGE_EXPIRATION_ROUTE_QUERY_KEY = "expiration";

export const YOUR_STUFF_EMPLOYEE_BADGES_PATH = "/your-stuff/employee/badges";

/** Shared SearchModal filter group (Your Responsibilities, Your Badges). */
export const EXPIRATION_SEARCH_MODAL_FILTER_GROUP = {
  label: "Expiration",
  items: [
    { label: "No Filter", value: "expiration_no_filter" },
    { label: "About to Expire", value: BADGE_EXPIRATION_FILTER_ABOUT_TO_EXPIRE },
  ],
};

export function isAboutToExpireExpirationFilter(filter) {
  return filter === BADGE_EXPIRATION_FILTER_ABOUT_TO_EXPIRE;
}

export function isAboutToExpireRouteQuery(query) {
  return query?.[BADGE_EXPIRATION_ROUTE_QUERY_KEY] === BADGE_EXPIRATION_FILTER_ABOUT_TO_EXPIRE;
}

/** True when expiration falls within the next `withinDays` days (inclusive of today). */
export function badgeExpiresWithinDays(
  badgeExpirationDate,
  withinDays = BADGE_ABOUT_TO_EXPIRE_WITHIN_DAYS
) {
  if (!badgeExpirationDate) {
    return false;
  }
  const exp = dayjs(badgeExpirationDate);
  if (!exp.isValid()) {
    return false;
  }
  const todayStart = dayjs().startOf("day");
  const windowEnd = todayStart.add(withinDays, "day").endOf("day");
  return (
    (exp.isAfter(todayStart) || exp.isSame(todayStart, "day")) &&
    (exp.isBefore(windowEnd) || exp.isSame(windowEnd, "day"))
  );
}

/** Alias used by responsibility assignment rows (`badge_expiration_date`). */
export function assignmentExpiresWithinSevenDays(badgeExpirationDate) {
  return badgeExpiresWithinDays(badgeExpirationDate, BADGE_ABOUT_TO_EXPIRE_WITHIN_DAYS);
}

export function badgeExpiresWithinSevenDays(badgeExpirationDate) {
  return assignmentExpiresWithinSevenDays(badgeExpirationDate);
}

export function filterCurrentBadgesAboutToExpire(
  badges,
  withinDays = BADGE_ABOUT_TO_EXPIRE_WITHIN_DAYS
) {
  return (badges || []).filter(
    (badge) => badge?.is_current && badgeExpiresWithinDays(badge.expiration_date, withinDays)
  );
}

export function countCurrentBadgesAboutToExpire(
  badges,
  withinDays = BADGE_ABOUT_TO_EXPIRE_WITHIN_DAYS
) {
  return filterCurrentBadgesAboutToExpire(badges, withinDays).length;
}

/**
 * Parse SearchModal checked items for an Expiration radio group.
 * @returns {"none"|"about_to_expire"}
 */
export function parseExpirationFilterFromSearchModalItems(checkedItems) {
  for (const item of checkedItems || []) {
    const val = typeof item === "string" ? item : item?.value;
    if (!val) continue;
    if (val === BADGE_EXPIRATION_FILTER_ABOUT_TO_EXPIRE || val === "About to Expire") {
      return BADGE_EXPIRATION_FILTER_ABOUT_TO_EXPIRE;
    }
    if (val === "expiration_no_filter" || val === "No Filter") {
      return BADGE_EXPIRATION_FILTER_NONE;
    }
    if (item?.labelType === "Expiration") {
      return val === "About to Expire"
        ? BADGE_EXPIRATION_FILTER_ABOUT_TO_EXPIRE
        : BADGE_EXPIRATION_FILTER_NONE;
    }
  }
  return BADGE_EXPIRATION_FILTER_NONE;
}

export function getYourStuffExpiringBadgesRoute() {
  return {
    path: YOUR_STUFF_EMPLOYEE_BADGES_PATH,
    query: { [BADGE_EXPIRATION_ROUTE_QUERY_KEY]: BADGE_EXPIRATION_FILTER_ABOUT_TO_EXPIRE },
  };
}

/** Route query key for deep-linking to Your Badges with badge standing filter. */
export const BADGE_STANDING_ROUTE_QUERY_KEY = "standing";

export const BADGE_STANDING_ROUTE_VALUE_OBSOLETE = "obsolete";

/** SearchModal standing checkboxes (Your Badges — same labels as Your Responsibilities). */
export const BADGE_STANDING_FILTER_OPTIONS = ["Given", "Good Standing", "Obsolete"];

export function isObsoleteStandingRouteQuery(query) {
  return query?.[BADGE_STANDING_ROUTE_QUERY_KEY] === BADGE_STANDING_ROUTE_VALUE_OBSOLETE;
}

export function badgeRowMatchesStandingFilters(badge, filters) {
  if (!filters?.length) {
    return true;
  }
  const status = String(badge?.status || "").toLowerCase();
  const issuedMethod = String(badge?.issued_method || "").toLowerCase();
  return filters.some((label) => {
    if (label === "Obsolete") {
      return status === "obsolete";
    }
    if (label === "Given") {
      return status === "given" || issuedMethod === "given";
    }
    if (label === "Good Standing") {
      if (status === "obsolete") {
        return false;
      }
      return status !== "given" && issuedMethod !== "given";
    }
    return false;
  });
}

export function filterBadgesByStanding(badges, standingFilters) {
  return (badges || []).filter((badge) => badgeRowMatchesStandingFilters(badge, standingFilters));
}

export function countCurrentBadgesObsolete(badges) {
  return (badges || []).filter(
    (badge) => badge?.is_current && badgeRowMatchesStandingFilters(badge, ["Obsolete"])
  ).length;
}

/**
 * Filters pre-selected on the first Your Responsibilities load.
 * Returns `null` when nothing should be auto-applied.
 * @returns {{ expiration: string, badgeStatuses: string[] }|null}
 */
export function resolveResponsibilityAutoFilters({
  aboutToExpireCount = 0,
  obsoleteCount = 0,
  expirationDismissed = false,
  standingDismissed = false,
} = {}) {
  const applyAboutToExpire = !expirationDismissed && aboutToExpireCount > 0;
  const applyObsolete = !standingDismissed && obsoleteCount > 0;
  if (!applyAboutToExpire && !applyObsolete) {
    return null;
  }
  return {
    expiration: applyAboutToExpire
      ? BADGE_EXPIRATION_FILTER_ABOUT_TO_EXPIRE
      : BADGE_EXPIRATION_FILTER_NONE,
    badgeStatuses: applyObsolete ? ["Obsolete"] : [],
  };
}

export function getYourStuffObsoleteBadgesRoute() {
  return {
    path: YOUR_STUFF_EMPLOYEE_BADGES_PATH,
    query: { [BADGE_STANDING_ROUTE_QUERY_KEY]: BADGE_STANDING_ROUTE_VALUE_OBSOLETE },
  };
}
